const KIT_SEARCH_STORAGE_KEY = "kit-search";
const KIT_SEARCH_STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * @typedef {Object} KitSearchPersistedState
 * @property {Record<string, string>} selections
 * @property {Record<string, string>} selectionLabels
 * @property {number} currentStep
 * @property {string} collectionPath
 * @property {number} timestamp
 */

/**
 * @param {string} storageKey
 * @returns {KitSearchPersistedState | null}
 */
function readKitSearchPersistedState(storageKey) {
  const storageKeys =
    storageKey === KIT_SEARCH_STORAGE_KEY
      ? [storageKey]
      : [storageKey, KIT_SEARCH_STORAGE_KEY];

  for (const key of storageKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        continue;
      }

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        continue;
      }

      const timestamp = Number(parsed.timestamp);
      if (
        !Number.isFinite(timestamp) ||
        Date.now() - timestamp > KIT_SEARCH_STORAGE_TTL_MS
      ) {
        localStorage.removeItem(key);
        continue;
      }

      const selections =
        parsed.selections && typeof parsed.selections === "object"
          ? parsed.selections
          : {};
      const selectionLabels =
        parsed.selectionLabels && typeof parsed.selectionLabels === "object"
          ? parsed.selectionLabels
          : {};

      if (key !== storageKey) {
        try {
          localStorage.setItem(storageKey, raw);
          localStorage.removeItem(key);
        } catch {
          // Ignore migration errors.
        }
      }

      return {
        selections,
        selectionLabels,
        currentStep: Number(parsed.currentStep) || 1,
        collectionPath: String(parsed.collectionPath || ""),
        timestamp,
      };
    } catch {
      // Try the next key.
    }
  }

  return null;
}

/**
 * Air control kit configurator — JSON API (get_options), theme-owned markup.
 * On localhost, uses assets/kit-search-fixtures.json (CORS-safe dev data).
 * @param {HTMLElement} root
 */
function initMainKitSearch(root) {
  const apiBase = root.dataset.acApiBase;
  const imagesBase = root.dataset.acImagesBase;
  const fixturesUrl = root.dataset.kitSearchFixturesUrl;
  const storageKey = root.dataset.kitSearchStorageKey || KIT_SEARCH_STORAGE_KEY;
  const errorText = root.dataset.loadErrorText || "";
  const stepLabelTemplate = root.dataset.stepLabelTemplate || "Step {{ step }}";
  const stepInstruction = root.dataset.stepInstruction || "";
  const editSelectionLabel =
    root.dataset.editSelectionLabel || "Edit {{ label }}";
  const stepNames = ["one", "two", "three", "four", "five"];

  const configurator = root.querySelector("[data-kit-search-configurator]");
  const optionTemplate = root.querySelector(
    "[data-kit-search-option-template]",
  );
  const summaryEl = root.querySelector("[data-kit-search-summary]");
  const summaryListEl = root.querySelector("[data-kit-search-summary-list]");
  const previousButton = root.querySelector("[data-kit-search-previous]");
  const nextButton = root.querySelector("[data-kit-search-next]");
  const submitButton = root.querySelector("[data-kit-search-submit]");

  if (!apiBase || !configurator || !optionTemplate) {
    return;
  }

  const stepElements = /** @type {HTMLElement[]} */ (
    [...configurator.querySelectorAll("[data-kit-search-step]")].sort(
      (a, b) =>
        Number(a.dataset.kitSearchStep) - Number(b.dataset.kitSearchStep),
    )
  );

  const maxSteps = stepElements.length;
  /** @type {Record<number, string>} */
  const selections = {};
  /** @type {Record<number, string>} */
  const selectionLabels = {};
  let collectionPath = "";
  let currentStep = 1;

  const isLocalDev =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  /** @type {Record<string, unknown> | null} */
  let fixtureResponses = null;

  const setBusy = (busy) => {
    configurator.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const clearPersistedState = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Ignore private mode / quota errors.
    }
  };

  const persistState = () => {
    try {
      /** @type {KitSearchPersistedState} */
      const payload = {
        selections: { ...selections },
        selectionLabels: { ...selectionLabels },
        currentStep,
        collectionPath,
        timestamp: Date.now(),
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Ignore private mode / quota errors.
    }
  };

  const resetSelections = () => {
    for (const key of Object.keys(selections)) {
      delete selections[key];
    }

    for (const key of Object.keys(selectionLabels)) {
      delete selectionLabels[key];
    }

    collectionPath = "";
    currentStep = 1;
  };

  /**
   * @param {HTMLElement} optionButton
   * @returns {string}
   */
  const getOptionDisplayLabel = (optionButton) => {
    const title =
      optionButton
        .querySelector("[data-kit-search-option-title]")
        ?.textContent?.trim() ?? "";

    if (title) {
      return title;
    }

    return (
      optionButton
        .querySelector("[data-kit-search-option-label]")
        ?.textContent?.trim() ?? ""
    );
  };

  /**
   * @param {string} template
   * @param {Record<string, string>} values
   * @returns {string}
   */
  const formatTemplate = (template, values) => {
    let result = template;

    for (const [key, value] of Object.entries(values)) {
      result = result.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "gi"), value);
    }

    return result;
  };

  const formatStepLabel = (step) => {
    const stepName = stepNames[step - 1] ?? String(step);
    return stepLabelTemplate.replace(/\{\{\s*step\s*\}\}/gi, stepName);
  };

  const normalizeImagesBase = (base) =>
    base.endsWith("/") ? base : `${base}/`;

  const buildFixtureKey = () => {
    const parts = [];

    for (let step = 1; step <= maxSteps; step++) {
      const id = selections[step];
      if (id) {
        parts.push(`option${step}=${id}`);
      }
    }

    return parts.join("&");
  };

  const buildOptionsUrl = () => {
    const base = apiBase.endsWith("/") ? apiBase : `${apiBase}/`;
    const url = new URL("get_options", base);
    url.searchParams.set("shopify_ajax", "1");

    for (let step = 1; step <= maxSteps; step++) {
      const id = selections[step];
      if (id) {
        url.searchParams.set(`option${step}`, id);
      }
    }

    return url.toString();
  };

  const loadFixtureResponses = async () => {
    if (fixtureResponses) {
      return fixtureResponses;
    }

    if (!fixturesUrl) {
      throw new Error("Fixture URL is not configured.");
    }

    const response = await fetch(fixturesUrl, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} for fixtures`);
    }

    const doc = await response.json();
    fixtureResponses = doc.responses ?? {};
    root.dataset.kitSearchDevMode = "fixtures";

    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info("[kit-search] Using local fixtures from", fixturesUrl);
    }

    return fixtureResponses;
  };

  const decodeHtml = (text) => {
    const el = document.createElement("textarea");
    el.innerHTML = text;
    return el.value;
  };

  const clearStep = (stepEl) => {
    const optionsEl = stepEl.querySelector("[data-kit-search-options]");
    const labelEl = stepEl.querySelector("[data-kit-search-step-label]");
    const instructionEl = stepEl.querySelector(
      "[data-kit-search-step-instruction]",
    );

    if (optionsEl) {
      optionsEl.replaceChildren();
    }

    if (labelEl) {
      labelEl.textContent = "";
    }

    if (instructionEl instanceof HTMLElement) {
      instructionEl.textContent = stepInstruction;
    }

    stepEl.hidden = true;
  };

  const clearStepsFrom = (fromStep) => {
    for (const stepEl of stepElements) {
      const step = Number(stepEl.dataset.kitSearchStep);
      if (step >= fromStep) {
        clearStep(stepEl);
      }
    }
  };

  const showStep = (stepNumber) => {
    for (const stepEl of stepElements) {
      const step = Number(stepEl.dataset.kitSearchStep);
      stepEl.hidden = step !== stepNumber;
    }
  };

  const scrollToActiveStep = () => {
    const stepEl = stepElements.find(
      (el) => Number(el.dataset.kitSearchStep) === currentStep,
    );

    if (!(stepEl instanceof HTMLElement) || stepEl.hidden) {
      return;
    }

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    requestAnimationFrame(() => {
      stepEl.scrollIntoView({
        block: "start",
        behavior: reduceMotion ? "auto" : "smooth",
      });
    });
  };

  const stepHasContent = (stepNumber) => {
    const stepEl = stepElements.find(
      (el) => Number(el.dataset.kitSearchStep) === stepNumber,
    );
    const optionsEl = stepEl?.querySelector("[data-kit-search-options]");
    return Boolean(optionsEl?.childElementCount);
  };

  const updateSelectionSummary = () => {
    if (
      !(summaryEl instanceof HTMLElement) ||
      !(summaryListEl instanceof HTMLElement)
    ) {
      return;
    }

    const isComplete = collectionPath.length > 0;
    summaryListEl.replaceChildren();

    /** @type {Array<{ step: number, label: string }>} */
    const items = [];

    for (let step = 1; step <= maxSteps; step++) {
      const id = selections[step];
      if (!id) {
        continue;
      }

      // Prior steps always; include the current step once the kit is complete.
      if (step < currentStep || (isComplete && step <= currentStep)) {
        items.push({
          step,
          label: selectionLabels[step] || id,
        });
      }
    }

    if (items.length === 0) {
      summaryEl.hidden = true;
      return;
    }

    summaryEl.hidden = false;

    for (const [index, item] of items.entries()) {
      if (index > 0) {
        const separator = document.createElement("span");
        separator.className = "main-kit-search__summary-separator";
        separator.setAttribute("aria-hidden", "true");
        separator.textContent = "/";
        summaryListEl.appendChild(separator);
      }

      const button = document.createElement("button");
      button.type = "button";
      button.className = "main-kit-search__summary-item";
      button.dataset.kitSearchSummaryStep = String(item.step);
      button.textContent = item.label;
      button.setAttribute(
        "aria-label",
        formatTemplate(editSelectionLabel, { label: item.label }),
      );
      summaryListEl.appendChild(button);
    }
  };

  const updateActionButtons = () => {
    const hasSelection = Boolean(selections[currentStep]);
    const isComplete = collectionPath.length > 0;
    // Keep Previous available on the generate SKU / submit step so users can revise.
    const canGoBack = currentStep > 1 || isComplete;

    if (previousButton instanceof HTMLButtonElement) {
      previousButton.hidden = !canGoBack;
      previousButton.disabled = !canGoBack;
      previousButton.classList.toggle("disabled", previousButton.disabled);
      previousButton.setAttribute(
        "aria-disabled",
        previousButton.disabled ? "true" : "false",
      );
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.hidden = isComplete;
      nextButton.disabled = !hasSelection || isComplete;
      nextButton.classList.toggle("disabled", nextButton.disabled);
      nextButton.setAttribute(
        "aria-disabled",
        nextButton.disabled ? "true" : "false",
      );
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.hidden = !isComplete;
      submitButton.disabled = !isComplete;
      submitButton.classList.toggle("disabled", submitButton.disabled);
      submitButton.setAttribute(
        "aria-disabled",
        submitButton.disabled ? "true" : "false",
      );
    }

    updateSelectionSummary();
  };

  const setStepSelection = (stepNumber, categoryId, optionButton) => {
    const stepEl = stepElements.find(
      (el) => Number(el.dataset.kitSearchStep) === stepNumber,
    );
    const optionsEl = stepEl?.querySelector("[data-kit-search-options]");

    if (optionsEl) {
      for (const button of optionsEl.querySelectorAll(
        "[data-kit-search-option]",
      )) {
        if (button instanceof HTMLButtonElement) {
          const isSelected = button === optionButton;
          button.classList.toggle("is-selected", isSelected);
          button.setAttribute("aria-pressed", isSelected ? "true" : "false");
        }
      }
    }
  };

  const restoreStepSelection = (stepNumber) => {
    const selectedId = selections[stepNumber];
    if (!selectedId) {
      return;
    }

    const stepEl = stepElements.find(
      (el) => Number(el.dataset.kitSearchStep) === stepNumber,
    );
    const optionsEl = stepEl?.querySelector("[data-kit-search-options]");
    const optionButton = optionsEl?.querySelector(
      `[data-kit-search-option][data-category-id="${selectedId}"]`,
    );

    if (optionButton instanceof HTMLButtonElement) {
      setStepSelection(stepNumber, selectedId, optionButton);
    }
  };

  const renderOption = (item) => {
    const fragment = optionTemplate.content.cloneNode(true);
    const option = fragment.querySelector("[data-kit-search-option]");
    const image = fragment.querySelector("[data-kit-search-option-image]");
    const title = fragment.querySelector("[data-kit-search-option-title]");
    const label = fragment.querySelector("[data-kit-search-option-label]");

    if (!(option instanceof HTMLButtonElement)) {
      return fragment;
    }

    const categoryId = String(item.CategoryID ?? "");
    const fileName = String(item.FileName ?? "");
    const categoryName = decodeHtml(String(item.CategoryName ?? "")).trim();
    const description = decodeHtml(
      String(item.Description ?? item.CategoryDescription ?? ""),
    );

    option.dataset.categoryId = categoryId;
    option.setAttribute("aria-pressed", "false");

    if (image instanceof HTMLImageElement) {
      image.src = `${normalizeImagesBase(imagesBase)}${fileName}`;
      image.alt = categoryName || description || "Option";
    }

    if (title) {
      title.textContent = categoryName;
    }

    if (label) {
      label.textContent = description;
    }

    return fragment;
  };

  const renderStep = (stepNumber, items) => {
    const stepEl = stepElements.find(
      (el) => Number(el.dataset.kitSearchStep) === stepNumber,
    );
    if (!stepEl) {
      return;
    }

    const labelEl = stepEl.querySelector("[data-kit-search-step-label]");
    const instructionEl = stepEl.querySelector(
      "[data-kit-search-step-instruction]",
    );
    const optionsEl = stepEl.querySelector("[data-kit-search-options]");

    if (!(optionsEl instanceof HTMLElement)) {
      return;
    }

    if (labelEl) {
      labelEl.textContent = formatStepLabel(stepNumber);
    }

    if (instructionEl instanceof HTMLElement && stepInstruction) {
      instructionEl.textContent = stepInstruction;
    }

    optionsEl.replaceChildren();

    for (const item of items) {
      optionsEl.appendChild(renderOption(item));
    }

    restoreStepSelection(stepNumber);

    root.dispatchEvent(
      new CustomEvent("kit-search:step-loaded", {
        bubbles: true,
        detail: { step: stepNumber, items },
      }),
    );
  };

  const showError = (message) => {
    clearStepsFrom(1);
    configurator.replaceChildren();
    const errorEl = document.createElement("p");
    errorEl.className = "main-kit-search__load-error";
    errorEl.textContent = message || errorText;
    configurator.appendChild(errorEl);
  };

  /**
   * @param {unknown} response
   * @returns {response is { collection_handle: string }}
   */
  const isCollectionResponse = (response) =>
    typeof response === "object" &&
    response !== null &&
    "collection_handle" in response &&
    typeof (
      /** @type {{ collection_handle: unknown }} */ (response).collection_handle
    ) === "string";

  /**
   * @param {unknown} response
   * @returns {response is Array<Record<string, unknown>>}
   */
  const isOptionsArray = (response) => Array.isArray(response);

  const fetchOptionsFromApi = async () => {
    const response = await fetch(buildOptionsUrl(), {
      method: "GET",
      credentials: "omit",
      mode: "cors",
      headers: { Accept: "application/json,*/*" },
    });

    if (!response.ok) {
      throw new Error(String(response.status));
    }

    return response.json();
  };

  const fetchOptionsFromFixtures = async () => {
    const responses = await loadFixtureResponses();
    const key = buildFixtureKey();

    if (key in responses) {
      return responses[key];
    }

    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        `[kit-search] No fixture for "${key || "step 1"}". Run scripts/fetch-kit-search-fixtures.sh to refresh.`,
      );
    }

    throw new Error(
      `No fixture for "${key || "step 1"}". Re-fetch assets/kit-search-fixtures.json or add this key.`,
    );
  };

  const fetchOptions = async () => {
    if (isLocalDev) {
      return fetchOptionsFromFixtures();
    }

    return fetchOptionsFromApi();
  };

  const applyOptionsResponse = (data) => {
    if (isCollectionResponse(data)) {
      collectionPath = data.collection_handle;
      showStep(currentStep);
      updateActionButtons();
      persistState();
      root.dispatchEvent(
        new CustomEvent("kit-search:complete", {
          bubbles: true,
          detail: { collectionPath },
        }),
      );
      return null;
    }

    if (!isOptionsArray(data)) {
      throw new Error("Invalid options response");
    }

    collectionPath = "";
    updateActionButtons();
    return data;
  };

  const loadStep = async (stepNumber) => {
    setBusy(true);

    try {
      const data = await fetchOptions();
      const items = applyOptionsResponse(data);

      if (items) {
        renderStep(stepNumber, items);
        currentStep = stepNumber;
        showStep(currentStep);
        updateActionButtons();
        persistState();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      showError(isLocalDev && message ? `${errorText} ${message}` : errorText);
    } finally {
      setBusy(false);
    }
  };

  const handleOptionSelect = (stepNumber, categoryId, optionButton) => {
    const previousSelection = selections[stepNumber];

    if (previousSelection === categoryId) {
      return;
    }

    setStepSelection(stepNumber, categoryId, optionButton);
    selections[stepNumber] = categoryId;
    selectionLabels[stepNumber] =
      getOptionDisplayLabel(optionButton) || categoryId;

    for (let step = stepNumber + 1; step <= maxSteps; step++) {
      delete selections[step];
      delete selectionLabels[step];
    }

    clearStepsFrom(stepNumber + 1);
    collectionPath = "";
    currentStep = stepNumber;
    showStep(currentStep);
    updateActionButtons();
    persistState();
  };

  /**
   * Jump back to an earlier step (from summary chips or equivalent).
   * Clears dependent later selections so the path can be revised.
   * @param {number} stepNumber
   */
  const goToStep = async (stepNumber) => {
    if (stepNumber < 1 || stepNumber > maxSteps) {
      return;
    }

    if (stepNumber === currentStep && !collectionPath) {
      return;
    }

    collectionPath = "";

    for (let step = stepNumber + 1; step <= maxSteps; step++) {
      delete selections[step];
      delete selectionLabels[step];
    }

    clearStepsFrom(stepNumber + 1);
    currentStep = stepNumber;

    if (!stepHasContent(currentStep)) {
      await loadStep(currentStep);
      scrollToActiveStep();
      return;
    }

    showStep(currentStep);
    updateActionButtons();
    persistState();
    scrollToActiveStep();
  };

  const handlePrevious = async () => {
    const wasComplete = collectionPath.length > 0;
    collectionPath = "";

    if (currentStep <= 1) {
      if (wasComplete) {
        updateActionButtons();
        persistState();
      }
      return;
    }

    currentStep -= 1;

    if (!stepHasContent(currentStep)) {
      await loadStep(currentStep);
      scrollToActiveStep();
      return;
    }

    showStep(currentStep);
    updateActionButtons();
    persistState();
    scrollToActiveStep();
  };

  const handleNext = async () => {
    if (!selections[currentStep]) {
      return;
    }

    setBusy(true);

    try {
      const data = await fetchOptions();
      const items = applyOptionsResponse(data);

      if (items) {
        const nextStep = currentStep + 1;
        renderStep(nextStep, items);
        currentStep = nextStep;
        showStep(currentStep);
      }

      scrollToActiveStep();
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      showError(isLocalDev && message ? `${errorText} ${message}` : errorText);
    } finally {
      setBusy(false);
      updateActionButtons();
      persistState();
    }
  };

  /**
   * @param {KitSearchPersistedState | null} persisted
   * @param {number} step
   * @returns {string}
   */
  const getPersistedSelectionId = (persisted, step) =>
    String(
      persisted?.selections?.[step] ??
        persisted?.selections?.[String(step)] ??
        "",
    ).trim();

  /**
   * @param {KitSearchPersistedState | null} persisted
   * @param {number} step
   * @returns {string}
   */
  const getPersistedSelectionLabel = (persisted, step) =>
    String(
      persisted?.selectionLabels?.[step] ??
        persisted?.selectionLabels?.[String(step)] ??
        "",
    ).trim();

  /**
   * @param {number} step
   * @param {string} savedId
   * @param {KitSearchPersistedState} persisted
   */
  const applyRestoredSelection = (step, savedId, persisted) => {
    selections[step] = savedId;
    restoreStepSelection(step);

    const stepEl = stepElements.find(
      (el) => Number(el.dataset.kitSearchStep) === step,
    );
    const optionButton = stepEl?.querySelector(
      `[data-kit-search-option][data-category-id="${savedId}"]`,
    );
    const fromDom =
      optionButton instanceof HTMLButtonElement
        ? getOptionDisplayLabel(optionButton)
        : "";

    selectionLabels[step] =
      getPersistedSelectionLabel(persisted, step) || fromDom || savedId;
  };

  const restorePersistedState = async () => {
    const persisted = readKitSearchPersistedState(storageKey);

    resetSelections();
    clearStepsFrom(1);

    if (!persisted || !getPersistedSelectionId(persisted, 1)) {
      await loadStep(1);
      return;
    }

    const savedCurrentStep = Math.min(
      Math.max(Number(persisted.currentStep) || 1, 1),
      maxSteps,
    );

    setBusy(true);

    try {
      for (let step = 1; step <= maxSteps; step++) {
        const data = await fetchOptions();
        const items = applyOptionsResponse(data);

        if (!items) {
          // Prior selections completed the kit (collection handle returned).
          currentStep = Math.max(step - 1, 1);
          showStep(currentStep);
          updateActionButtons();
          persistState();
          return;
        }

        renderStep(step, items);

        const savedId = getPersistedSelectionId(persisted, step);

        if (!savedId) {
          // Options for this step loaded; user had not selected here yet.
          currentStep = savedCurrentStep >= step ? step : Math.max(step - 1, 1);
          showStep(currentStep);
          updateActionButtons();
          persistState();
          return;
        }

        const optionExists = items.some(
          (item) => String(item.CategoryID ?? "") === savedId,
        );

        if (!optionExists) {
          throw new Error("Stale kit search selection");
        }

        applyRestoredSelection(step, savedId, persisted);
        currentStep = step;
      }

      // All steps have selections — resolve a final collection handle if ready.
      const finalData = await fetchOptions();
      applyOptionsResponse(finalData);

      if (stepHasContent(savedCurrentStep)) {
        currentStep = savedCurrentStep;
      }

      showStep(currentStep);
      updateActionButtons();
      persistState();
    } catch {
      clearPersistedState();
      resetSelections();
      clearStepsFrom(1);
      await loadStep(1);
    } finally {
      setBusy(false);
    }
  };

  configurator.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const summaryItem = target.closest("[data-kit-search-summary-step]");
    if (summaryItem instanceof HTMLButtonElement) {
      const summaryStep = Number(summaryItem.dataset.kitSearchSummaryStep);
      if (summaryStep) {
        event.preventDefault();
        void goToStep(summaryStep);
      }
      return;
    }

    const option = target.closest("[data-kit-search-option]");
    if (!(option instanceof HTMLButtonElement)) {
      return;
    }

    const stepEl = option.closest("[data-kit-search-step]");
    if (!stepEl) {
      return;
    }

    const stepNumber = Number(stepEl.dataset.kitSearchStep);
    const categoryId = option.dataset.categoryId;

    if (!stepNumber || !categoryId || stepNumber !== currentStep) {
      return;
    }

    event.preventDefault();
    handleOptionSelect(stepNumber, categoryId, option);
  });

  if (previousButton) {
    previousButton.addEventListener("click", () => {
      void handlePrevious();
    });
  }

  if (nextButton) {
    nextButton.addEventListener("click", () => {
      void handleNext();
    });
  }

  if (submitButton) {
    submitButton.addEventListener("click", () => {
      if (!collectionPath) {
        return;
      }

      const destination = collectionPath.startsWith("http")
        ? collectionPath
        : `${window.location.origin}${collectionPath}`;

      if (window.top && window.top !== window) {
        window.top.location.href = destination;
      } else {
        window.location.href = destination;
      }
    });
  }

  updateActionButtons();
  void restorePersistedState();
}

function boot() {
  document
    .querySelectorAll("[data-main-kit-search-root]")
    .forEach(initMainKitSearch);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
